import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import http from '@/services/http'
import { usePermissionsStore } from '@/stores/permissions.store'
import type {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  MeResponse,
  PlanFeatures,
  Profile,
  RefreshRequest,
  User,
} from '@/types/api'

export const useAuthStore = defineStore(
  'auth',
  () => {
    const accessToken = ref<string | null>(null)
    const refreshToken = ref<string | null>(null)
    const user = ref<User | null>(null)
    const selectedProfile = ref<Profile | null>(null)
    const planFeatures = ref<PlanFeatures>({})
    /** id del super-admin cuando la sesión es de soporte (impersonación); null si no. */
    const impersonatedBy = ref<number | null>(null)

    const isAuthenticated = computed(() => !!accessToken.value)
    const userId = computed(() => user.value?.id ?? null)
    const username = computed(() => user.value?.username ?? null)
    const isSupport = computed(() => impersonatedBy.value !== null)

    /**
     * ¿El plan del bufete incluye esta función? Sin dato (contexto plataforma o
     * plan sin restricción) se asume habilitada — el backend es el gate real.
     */
    const hasFeature = (feature: string): boolean => planFeatures.value[feature] !== false

    const applyLoginPayload = (payload: LoginResponse) => {
      accessToken.value = payload.token ?? null
      refreshToken.value = payload.refresh_token ?? null
      user.value = payload.user ?? null
      if (payload.user?.profiles?.length === 1) {
        selectedProfile.value = payload.user.profiles[0]
      }
      planFeatures.value = payload.plan_features ?? {}
      impersonatedBy.value = null
      const permissionsStore = usePermissionsStore()
      permissionsStore.set(payload.permissions ?? {})
    }

    /** Inicia una sesión de SOPORTE (impersonación) a partir del canje. */
    function applySupportSession(payload: {
      token: string
      user: User
      permissions?: Record<string, string[]>
      plan_features?: PlanFeatures
      impersonated_by: number
    }) {
      accessToken.value = payload.token
      refreshToken.value = null // sesión corta, sin refresh
      user.value = payload.user
      selectedProfile.value = payload.user?.profiles?.[0] ?? null
      planFeatures.value = payload.plan_features ?? {}
      impersonatedBy.value = payload.impersonated_by
      const permissionsStore = usePermissionsStore()
      permissionsStore.set(payload.permissions ?? {})
    }

    async function login(
      usernameInput: string,
      password: string,
      profileSlug?: string,
    ): Promise<LoginResponse> {
      const body: LoginRequest = {
        username: usernameInput,
        password,
        profile_slug: profileSlug,
      }
      const { data } = await http.post<ApiResponse<LoginResponse>>('/auth/login', body)
      // select_profile llega en la raíz (sin envolver en data); el login normal
      // llega envuelto. Tolerar ambas formas.
      const payload = (data.data ?? data) as LoginResponse

      // El backend puede pedir que el usuario elija un perfil antes de emitir el token.
      if (payload.status === 'select_profile') {
        return payload
      }

      applyLoginPayload(payload)
      return payload
    }

    async function refresh(): Promise<string> {
      if (!refreshToken.value) throw new Error('No refresh token available')
      const body: RefreshRequest = { refresh_token: refreshToken.value }
      const { data } = await http.post<ApiResponse<LoginResponse>>('/auth/refresh', body)
      const payload = data.data as LoginResponse
      accessToken.value = payload.token ?? null
      if (payload.refresh_token) refreshToken.value = payload.refresh_token
      return accessToken.value as string
    }

    async function logout(): Promise<void> {
      try {
        await http.post('/auth/logout')
      } finally {
        accessToken.value = null
        refreshToken.value = null
        user.value = null
        selectedProfile.value = null
        planFeatures.value = {}
        impersonatedBy.value = null
        const permissionsStore = usePermissionsStore()
        permissionsStore.clear()
      }
    }

    async function fetchMe(): Promise<MeResponse> {
      const { data } = await http.get<ApiResponse<MeResponse>>('/auth/me')
      const payload = data.data as MeResponse
      user.value = payload.user
      planFeatures.value = payload.plan_features ?? {}
      const permissionsStore = usePermissionsStore()
      permissionsStore.set(payload.permissions ?? {})
      if (!selectedProfile.value && payload.user.profiles?.length) {
        selectedProfile.value = payload.user.profiles[0]
      }
      return payload
    }

    function setSelectedProfile(profile: Profile | null): void {
      selectedProfile.value = profile
    }

    return {
      // state
      accessToken,
      refreshToken,
      user,
      selectedProfile,
      planFeatures,
      impersonatedBy,
      // getters
      isAuthenticated,
      userId,
      username,
      isSupport,
      hasFeature,
      // actions
      login,
      refresh,
      logout,
      fetchMe,
      applySupportSession,
      setSelectedProfile,
    }
  },
  {
    persist: {
      key: 'tpl.auth',
      pick: ['accessToken', 'refreshToken', 'selectedProfile', 'planFeatures', 'impersonatedBy'],
    },
  },
)
